/**
 * Portalless modal — position fixed overlay + centred card.
 * Escape + backdrop click close. Stays simple: no @radix/portal, no focus trap.
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Modal({ open, onClose, title, children, size = 'md' }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', onKey)
    // Lock background scroll while open.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    // Tamaño extra para modales con tablas anchas (Nueva devolución,
    // Confirmar review). Se acerca al ancho de la pantalla en desktop.
    '2xl': 'max-w-6xl',
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className={cn(
          'w-full bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]',
          widths[size]
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-expo-dark">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-expo-dark transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

/**
 * Form-group helpers so pages don't repeat label/input styling.
 */
export function Field({ label, children, hint, required }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-expo-dark mb-1.5">
        {label}
        {required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </span>
      {children}
      {hint ? <span className="block text-xs text-gray-500 mt-1">{hint}</span> : null}
    </label>
  )
}

export function Input({ className, ...props }) {
  return (
    <input
      className={cn(
        'w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:border-expo-primary focus:ring-2 focus:ring-expo-primary/20 outline-none transition-colors',
        className
      )}
      {...props}
    />
  )
}

export function Select({ className, children, ...props }) {
  return (
    <select
      className={cn(
        'w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:border-expo-primary focus:ring-2 focus:ring-expo-primary/20 outline-none transition-colors bg-white',
        className
      )}
      {...props}
    >
      {children}
    </select>
  )
}
